import { FC, memo, useEffect } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { Card, Empty, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

const { Title } = Typography;

const ITEM_HEIGHT = 250;

interface IProps {
  data: {
    date: string;
    value: number;
  }[];
  total: number;
}

const AppointmentsMonthly: FC<IProps> = ({ data, total }) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!data?.length) {
      return;
    }

    const root = am5.Root.new('AppointmentsMonthly');

    // https://www.amcharts.com/docs/v5/concepts/themes/
    root.setThemes([am5themes_Animated.new(root)]);

    root.dateFormatter.setAll({
      dateFormat: 'yyyy-mm',
      dateFields: ['valueX']
    });

    // Create chart
    // https://www.amcharts.com/docs/v5/charts/xy-chart/
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        focusable: true,
        panX: true,
        panY: true,
        wheelX: 'panX',
        wheelY: 'zoomX',
        pinchZoomX: true,
        paddingLeft: 0
      })
    );

    // Create axes
    // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        maxDeviation: 0.1,
        groupData: false,
        baseInterval: {
          timeUnit: 'month',
          count: 1
        },
        renderer: am5xy.AxisRendererX.new(root, {
          minorGridEnabled: true,
          minGridDistance: 70
        }),
        tooltip: am5.Tooltip.new(root, {})
      })
    );

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        maxDeviation: 0.2,
        renderer: am5xy.AxisRendererY.new(root, {})
      })
    );

    // Add series
    // https://www.amcharts.com/docs/v5/charts/xy-chart/series/
    const series = chart.series.push(
      am5xy.LineSeries.new(root, {
        minBulletDistance: 10,
        connect: false,
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'value',
        valueXField: 'date',
        tooltip: am5.Tooltip.new(root, {
          pointerOrientation: 'horizontal',
          labelText: '{valueY}'
        })
      })
    );

    series.fills.template.setAll({
      fillOpacity: 0.2,
      visible: true
    });

    series.strokes.template.setAll({
      strokeWidth: 2
    });

    // Set up data processor to parse string dates
    // https://www.amcharts.com/docs/v5/concepts/data/#Pre_processing_data
    series.data.processor = am5.DataProcessor.new(root, {
      dateFormat: 'yyyy-MM-dd',
      dateFields: ['date']
    });

    series.data.setAll(data);

    series.bullets.push(function () {
      const circle = am5.Circle.new(root, {
        radius: 4,
        fill: root.interfaceColors.get('background'),
        stroke: series.get('fill'),
        strokeWidth: 2
      });

      return am5.Bullet.new(root, {
        sprite: circle
      });
    });

    // Add cursor
    // https://www.amcharts.com/docs/v5/charts/xy-chart/cursor/
    const cursor = chart.set(
      'cursor',
      am5xy.XYCursor.new(root, {
        xAxis: xAxis,
        behavior: 'none'
      })
    );

    cursor.lineY.set('visible', false);

    // add scrollbar
    chart.set(
      'scrollbarX',
      am5.Scrollbar.new(root, {
        orientation: 'horizontal'
      })
    );

    chart.appear(1000, 100);

    return () => root.dispose();
  }, [data]);

  return (
    <Card
      title={
        <>
          <Title level={5} style={{ marginTop: 16 }}>
            {t('dashboard.appointments_monthly')}
          </Title>
          <Title level={2} style={{ marginTop: 4 }}>
            {t('dashboard.total', { count: total })}
          </Title>
        </>
      }
    >
      {data.length ? (
        <div id="AppointmentsMonthly" style={{ height: ITEM_HEIGHT }} />
      ) : (
        <Empty style={{ height: ITEM_HEIGHT }} />
      )}
    </Card>
  );
};

export default memo(AppointmentsMonthly);
